import { BiX, BiUser, BiCalendar, BiMoney } from 'react-icons/bi'
import { Agent } from '../services/agentService'
import { Agency } from '../services/agencyService'

interface AgentDetailsModalProps {
  agent: Agent | null
  agency: Agency | null
  isOpen: boolean
  onClose: () => void
}

const AgentDetailsModal: React.FC<AgentDetailsModalProps> = ({ agent, agency, isOpen, onClose }) => {
  if (!isOpen || !agent) return null

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatDateOnly = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  // Calculate prorated payment information
  const calculateProratedInfo = () => {
    if (!agency || !agent) return null

    // Only show for licensed agents where agency is paying
    if (agent.license.status !== 'licensed' || agent.paymentResponsibility !== 'agency') {
      return null
    }

    // Get the agency's subscription info
    const subscriptions = agency.billing?.subscriptions || []
    if (subscriptions.length === 0) return null

    // Get the latest active subscription
    const latestSubscription = subscriptions
      .filter(sub => sub.stripeSubscriptionStatus)
      .sort((a, b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime())[0]

    if (!latestSubscription) return null

    // Find the agent seat product
    const seatProduct = latestSubscription.stripeProducts?.find(product => product.productType === 'agent_seat')
    if (!seatProduct) return null

    const seatCostPerMonth = seatProduct.unitPrice // in pence
    const seatCostPounds = seatCostPerMonth / 100

    // Get the subscription billing cycle start date
    const subscriptionStartDate = new Date(latestSubscription.createdAt || '')

    // Get when the agent became licensed
    const agentLicensedDate = new Date(agent.license.updatedAt)

    // If agent was licensed before or on subscription start, no proration
    if (agentLicensedDate <= subscriptionStartDate) {
      return {
        wasProrated: false,
        monthlyCost: seatCostPounds,
        subscriptionStartDate,
        agentLicensedDate
      }
    }

    // Calculate days in the billing cycle (assume 30 days for monthly)
    const daysInCycle = 30

    // Calculate days remaining in the cycle when agent was licensed
    const msPerDay = 24 * 60 * 60 * 1000
    const daysSinceSubscriptionStart = Math.floor((agentLicensedDate.getTime() - subscriptionStartDate.getTime()) / msPerDay)
    const daysRemainingInCycle = daysInCycle - (daysSinceSubscriptionStart % daysInCycle)

    // Calculate prorated amount
    const proratedAmount = (seatCostPounds * daysRemainingInCycle) / daysInCycle
    const proratedPercentage = (daysRemainingInCycle / daysInCycle) * 100

    return {
      wasProrated: true,
      monthlyCost: seatCostPounds,
      proratedAmount,
      proratedPercentage,
      daysRemainingInCycle,
      daysInCycle,
      subscriptionStartDate,
      agentLicensedDate
    }
  }

  const proratedInfo = calculateProratedInfo()

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                <BiUser className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                  Agent Details
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {agent.user?.firstName && agent.user?.lastName
                    ? `${agent.user.firstName} ${agent.user.lastName}`
                    : 'Unknown User'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
            >
              <BiX size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Agent Information */}
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider mb-3">
                Agent Information
              </h3>
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600 dark:text-slate-400">Email</span>
                  <span className="text-sm font-medium text-slate-900 dark:text-white">
                    {agent.user?.email || 'Not provided'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600 dark:text-slate-400">Role</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${agent.role === 'admin'
                      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
                      : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                    }`}>
                    {agent.role === 'admin' ? 'Admin' : 'Agent'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600 dark:text-slate-400">Payment Responsibility</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${agent.paymentResponsibility === 'agency'
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                      : agent.paymentResponsibility === 'agent'
                        ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                    }`}>
                    {agent.paymentResponsibility === 'agency' ? 'Agency Pays' :
                      agent.paymentResponsibility === 'agent' ? 'Agent Pays' :
                        'No Payment Required'}
                  </span>
                </div>
              </div>
            </div>

            {/* Invite Information */}
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider mb-3 flex items-center">
                <BiCalendar className="w-4 h-4 mr-2" />
                Invite Information
              </h3>
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <span className="text-sm text-slate-600 dark:text-slate-400">Status</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${agent.invite.status === 'accepted'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                      : agent.invite.status === 'revoked'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                        : agent.invite.status === 'declined'
                          ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                    }`}>
                    {agent.invite.status === 'accepted' ? 'Accepted' :
                      agent.invite.status === 'revoked' ? 'Revoked' :
                        agent.invite.status === 'declined' ? 'Declined' :
                          agent.invite.status === 'requested' ? 'Requested' : 'Pending'}
                  </span>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-sm text-slate-600 dark:text-slate-400">Invited On</span>
                  <div className="text-right">
                    <div className="text-sm font-medium text-slate-900 dark:text-white">
                      {formatDateOnly(agent.invite.createdAt)}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {new Date(agent.invite.createdAt).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    {agent.invite.status === 'accepted' ? 'Accepted On' :
                      agent.invite.status === 'declined' ? 'Declined On' :
                        agent.invite.status === 'revoked' ? 'Revoked On' : 'Last Updated'}
                  </span>
                  <div className="text-right">
                    <div className="text-sm font-medium text-slate-900 dark:text-white">
                      {formatDateOnly(agent.invite.updatedAt)}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {new Date(agent.invite.updatedAt).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                </div>
                {agent.invite.createdAt !== agent.invite.updatedAt && (
                  <div className="pt-2 border-t border-slate-200 dark:border-slate-600">
                    <div className="text-xs text-slate-500 dark:text-slate-400 italic">
                      {agent.invite.status === 'accepted' && 'Agent accepted the invitation'}
                      {agent.invite.status === 'declined' && 'Agent declined the invitation'}
                      {agent.invite.status === 'revoked' && 'Invitation was revoked by admin'}
                      {agent.invite.status === 'requested' && 'Agent requested to join the agency'}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* License Information */}
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider mb-3 flex items-center">
                <BiCalendar className="w-4 h-4 mr-2" />
                License Information
              </h3>
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <span className="text-sm text-slate-600 dark:text-slate-400">Status</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${agent.license.status === 'licensed'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                      : agent.license.status === 'trial'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                        : 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300'
                    }`}>
                    {agent.license.status === 'licensed' ? 'Licensed' :
                      agent.license.status === 'trial' ? 'Trial' : 'No License'}
                  </span>
                </div>

                {agent.license.status !== 'none' && (
                  <>
                    <div className="flex justify-between items-start">
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        {agent.license.status === 'trial' ? 'Trial Started' : 'License Created'}
                      </span>
                      <div className="text-right">
                        <div className="text-sm font-medium text-slate-900 dark:text-white">
                          {formatDateOnly(agent.license.createdAt)}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {new Date(agent.license.createdAt).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between items-start">
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        {agent.license.status === 'trial' ? 'Trial Ended / Last Updated' :
                          agent.license.status === 'licensed' ? 'Started Paying' : 'Last Updated'}
                      </span>
                      <div className="text-right">
                        <div className="text-sm font-medium text-slate-900 dark:text-white">
                          {formatDateOnly(agent.license.updatedAt)}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {new Date(agent.license.updatedAt).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    </div>

                    {agent.license.status === 'licensed' && agent.license.createdAt !== agent.license.updatedAt && (
                      <div className="pt-2 border-t border-slate-200 dark:border-slate-600">
                        <div className="text-xs text-slate-500 dark:text-slate-400 italic">
                          {agent.paymentResponsibility === 'agency'
                            ? 'Agency is paying for this agent\'s license'
                            : agent.paymentResponsibility === 'agent'
                              ? 'Agent is self-paying for their license'
                              : 'No payment required for this license'}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Prorated Payment Information */}
            {proratedInfo && agent.license.status === 'licensed' && agent.paymentResponsibility === 'agency' && (
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider mb-3 flex items-center">
                  <BiMoney className="w-4 h-4 mr-2" />
                  Payment Information
                </h3>
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-slate-600 dark:text-slate-400">Monthly Seat Cost</span>
                    <span className="text-sm font-medium text-slate-900 dark:text-white">
                      £{proratedInfo.monthlyCost.toFixed(2)}
                    </span>
                  </div>

                  {proratedInfo.wasProrated ? (
                    <>
                      <div className="flex justify-between items-start">
                        <span className="text-sm text-slate-600 dark:text-slate-400">Initial Prorated Charge</span>
                        <div className="text-right">
                          <div className="text-sm font-medium text-orange-600 dark:text-orange-400">
                            £{proratedInfo.proratedAmount.toFixed(2)}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            ~{proratedInfo.proratedPercentage.toFixed(0)}% of monthly cost
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between items-start">
                        <span className="text-sm text-slate-600 dark:text-slate-400">Days Covered (Initial)</span>
                        <span className="text-sm font-medium text-slate-900 dark:text-white">
                          {proratedInfo.daysRemainingInCycle} of {proratedInfo.daysInCycle} days
                        </span>
                      </div>
                      <div className="pt-2 border-t border-slate-200 dark:border-slate-600">
                        <div className="text-xs text-slate-500 dark:text-slate-400 space-y-1">
                          <p className="italic">
                            <strong>Subscription started:</strong> {formatDateOnly(proratedInfo.subscriptionStartDate.toISOString())}
                          </p>
                          <p className="italic">
                            <strong>Agent licensed:</strong> {formatDateOnly(proratedInfo.agentLicensedDate.toISOString())}
                          </p>
                          <p className="mt-2 text-amber-600 dark:text-amber-400">
                            ℹ️ This agent was added mid-billing cycle. The agency was charged a prorated amount (£{proratedInfo.proratedAmount.toFixed(2)}) for the remaining {proratedInfo.daysRemainingInCycle} days, then the full monthly amount (£{proratedInfo.monthlyCost.toFixed(2)}) from the next billing cycle.
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="pt-2 border-t border-slate-200 dark:border-slate-600">
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        <p className="italic">
                          This agent was licensed at or before the subscription started. No prorated charges were applied.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end p-6 border-t border-slate-200 dark:border-slate-700">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default AgentDetailsModal
